import { apiBaseUrl, culture } from "./config";
import { authStore } from "./auth-store";
import type {
  AnalyzerConversationDto,
  AnalyzerMessageDto,
  AppointmentBookingResponse,
  AppointmentDto,
  AskAnalyzerResponse,
  BlueprintAccessResponse,
  CheckoutResponse,
  ClientConfigResponse,
  ConfirmAppointmentRequest,
  ContactRequest,
  CreditBalanceDto,
  CreditPackageDto,
  CreditTransactionDto,
  DealSourcingFeeDto,
  BlueprintCategoryDto,
  AnalyzerPricingDto,
  MentorshipOptionsDto,
  MemberBlueprintDto,
  MemberOverviewDto,
  MemberSubscriptionDto,
  MemberVideosResponse,
  MembershipPlanDto,
  PagedResult,
  PaymentDto,
  ProfileResponse,
  RegisterRequest,
  SubscriptionDto,
  TokenResponse,
  UpdateProfileRequest,
} from "./types";

/** Header the API's [RequireTurnstile] filter reads (AppHeaders.Turnstile). */
export const TURNSTILE_HEADER = "X-Turnstile-Token";

/** RFC 9457 problem details returned by the API (GlobalExceptionHandler). */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  errorKey?: string;
  traceId?: string;
  errors?: Record<string, string[]>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly errorKey: string | undefined;
  readonly traceId: string | undefined;
  readonly errors: Record<string, string[]>;
  readonly title: string | undefined;
  readonly detail: string | undefined;

  constructor(status: number, problem: ProblemDetails | null, fallback: string) {
    super(problem?.detail || problem?.title || fallback);
    this.name = "ApiError";
    this.status = status;
    this.errorKey = problem?.errorKey;
    this.traceId = problem?.traceId;
    this.errors = problem?.errors ?? {};
    this.title = problem?.title;
    this.detail = problem?.detail;
  }

  /** Validation messages of one field (server keys are PascalCase, e.g. "Email"). */
  field(name: string): string[] | undefined {
    const key = Object.keys(this.errors).find((k) => k.toLowerCase() === name.toLowerCase());
    return key ? this.errors[key] : undefined;
  }

  /** Every message: the summary plus each validation message. */
  get allMessages(): string[] {
    const fieldMessages = Object.values(this.errors).flat();
    if (fieldMessages.length) return fieldMessages;
    return [this.message];
  }

  get isInsufficientCredits() {
    return this.errorKey === "InsufficientCredits" || this.status === 402;
  }
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.allMessages.join(" ");
  if (error instanceof Error) return error.message;
  return "Something went wrong.";
}

// ---------------- Token refresh (single flight) ----------------

const REFRESH_MARGIN_MS = 30_000;
let refreshing: Promise<boolean> | null = null;

async function rawFetch(path: string, init: RequestInit) {
  try {
    return await fetch(apiBaseUrl() + path, init);
  } catch {
    throw new ApiError(0, null, "Cannot reach the server. Check your connection and try again.");
  }
}

/**
 * Exchanges the refresh token for a new pair. Concurrent callers share one request; a call made after another
 * caller already refreshed (token differs from `failedToken`) returns immediately.
 */
export function refreshTokens(failedToken?: string | null): Promise<boolean> {
  const current = authStore.get();
  if (
    failedToken !== undefined &&
    current.accessToken &&
    current.accessToken !== failedToken &&
    current.expiresAt - Date.now() > REFRESH_MARGIN_MS
  ) {
    return Promise.resolve(true);
  }
  if (refreshing) return refreshing;

  refreshing = (async () => {
    const refreshToken = authStore.get().refreshToken;
    if (!refreshToken) {
      authStore.clear();
      return false;
    }
    try {
      const response = await rawFetch("api/v1/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept-Language": culture },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        // 400/401: the refresh token is invalid, expired or revoked (or the subscription expired).
        if (response.status >= 400 && response.status < 500) authStore.clear();
        return false;
      }
      authStore.setTokens((await response.json()) as TokenResponse);
      return true;
    } catch {
      return false; // Network error: keep the session, the next call tries again.
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

async function accessToken(): Promise<string | null> {
  const s = authStore.get();
  if (!s.refreshToken) return null;
  if (!s.accessToken || s.expiresAt - Date.now() < REFRESH_MARGIN_MS) {
    await refreshTokens(s.accessToken);
  }
  return authStore.get().accessToken;
}

// ---------------- Requests ----------------

export interface RequestOptions {
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Send the access token (default true; anonymous endpoints simply ignore it). */
  auth?: boolean;
  turnstileToken?: string | null;
  signal?: AbortSignal;
}

function buildPath(path: string, query?: RequestOptions["query"]) {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const url = buildPath(`api/v1/${path}`, options.query);
  const useAuth = options.auth !== false;

  const send = async (token: string | null) => {
    const headers: Record<string, string> = { Accept: "application/json", "Accept-Language": culture };
    if (options.body !== undefined) headers["Content-Type"] = "application/json";
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (options.turnstileToken) headers[TURNSTILE_HEADER] = options.turnstileToken;
    return rawFetch(url, {
      method,
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : null,
      signal: options.signal ?? null,
    });
  };

  let token = useAuth ? await accessToken() : null;
  let response = await send(token);
  if (response.status === 401 && token) {
    // Expired/revoked access token: refresh once (shared with concurrent requests) and retry.
    if (await refreshTokens(token)) {
      token = authStore.get().accessToken;
      response = await send(token);
    }
  }

  if (!response.ok) {
    let problem: ProblemDetails | null = null;
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      problem = null;
    }
    throw new ApiError(response.status, problem, `Request failed (${response.status}).`);
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

const get = <T>(path: string, options?: RequestOptions) => request<T>("GET", path, options);
const post = <T>(path: string, body?: unknown, options?: RequestOptions) =>
  request<T>("POST", path, { ...options, body: body ?? {} });

// ---------------- Endpoints ----------------

export const api = {
  config: () => get<ClientConfigResponse>("config", { auth: false }),

  auth: {
    async login(email: string, password: string, turnstileToken?: string | null) {
      const tokens = await post<TokenResponse>(
        "auth/login",
        { email, password },
        { auth: false, turnstileToken: turnstileToken ?? null },
      );
      authStore.setTokens(tokens);
      return tokens;
    },
    async logout() {
      const refreshToken = authStore.get().refreshToken;
      if (refreshToken) {
        try {
          await post("auth/logout", { refreshToken }, { auth: false });
        } catch {
          // Signing out locally is enough when the API is unreachable.
        }
      }
      authStore.clear();
    },
    register: (body: RegisterRequest, turnstileToken?: string | null) =>
      post<{ id: string }>("auth/register", body, { auth: false, turnstileToken: turnstileToken ?? null }),
    confirmEmail: (userId: string, code: string) =>
      post<void>("auth/confirm-email", { userId, code }, { auth: false }),
    resendConfirmation: (email: string, turnstileToken?: string | null) =>
      post<void>("auth/resend-confirmation", { email }, { auth: false, turnstileToken: turnstileToken ?? null }),
    forgotPassword: (email: string, turnstileToken?: string | null) =>
      post<void>("auth/forgot-password", { email }, { auth: false, turnstileToken: turnstileToken ?? null }),
    resetPassword: (body: { email: string; code: string; password: string; confirmPassword: string }) =>
      post<void>("auth/reset-password", body, { auth: false }),
  },

  account: {
    profile: () => get<ProfileResponse>("account/profile"),
    updateProfile: (body: UpdateProfileRequest) => request<void>("PUT", "account/profile", { body }),
    changePassword: (body: { currentPassword: string; newPassword: string; confirmNewPassword: string }) =>
      post<void>("account/change-password", body),
    changeEmail: (newEmail: string) => post<void>("account/change-email", { newEmail }),
    confirmEmailChange: (userId: string, email: string, code: string) =>
      post<void>("account/confirm-email-change", { userId, email, code }, { auth: false }),
    deleteAccount: (password: string) => post<void>("account/delete", { password }),
  },

  blueprints: {
    list: () => get<MemberBlueprintDto[]>("blueprints", { auth: false }),
    categories: () => get<BlueprintCategoryDto[]>("blueprints/categories", { auth: false }),
    access: (id: string) => get<BlueprintAccessResponse>(`blueprints/${id}/access`),
  },

  academy: {
    videos: () => get<MemberVideosResponse>("academy/videos"),
  },

  dealSourcing: {
    fees: () => get<DealSourcingFeeDto[]>("deal-sourcing/fees", { auth: false }),
  },

  mentorship: {
    packages: () => get<CreditPackageDto[]>("mentorship/packages", { auth: false }),
    appointments: (page = 1, pageSize = 50) =>
      get<PagedResult<AppointmentDto>>("mentorship/appointments", {
        query: { page, pageSize },
      }),
    options: () => get<MentorshipOptionsDto>("mentorship/options", { auth: false }),
    book: (topic: string | null, durationMinutes: number) =>
      post<AppointmentBookingResponse>("mentorship/appointments/book", { topic, durationMinutes }),
    /** The Cal.com embed reported a booking (or a reschedule): applied at once. */
    syncCalBooking: (id: string, bookingUid: string) =>
      post<AppointmentDto>(`mentorship/appointments/${id}/calcom-booking`, { bookingUid }),
    confirm: (id: string, body: ConfirmAppointmentRequest) =>
      post<AppointmentDto>(`mentorship/appointments/${id}/confirm`, body),
    reschedule: (id: string) => post<AppointmentBookingResponse>(`mentorship/appointments/${id}/reschedule`),
    requestCancel: (id: string, note: string | null) =>
      post<AppointmentDto>(`mentorship/appointments/${id}/cancel-request`, { note }),
    requestChange: (id: string, note: string | null) =>
      post<AppointmentDto>(`mentorship/appointments/${id}/change-request`, { note }),
    discard: (id: string) => request<void>("DELETE", `mentorship/appointments/${id}`),
  },

  analyzer: {
    packages: () => get<CreditPackageDto[]>("analyzer/packages", { auth: false }),
    pricing: () => get<AnalyzerPricingDto>("analyzer/pricing", { auth: false }),
    conversations: () => get<AnalyzerConversationDto[]>("analyzer/conversations"),
    messages: (conversationId: string) =>
      get<PagedResult<AnalyzerMessageDto>>("analyzer/messages", {
        query: { conversationId, page: 1, pageSize: 200 },
      }),
    ask: (question: string, conversationId: string | null) =>
      post<AskAnalyzerResponse>("analyzer/ask", { question, conversationId }),
  },

  billing: {
    plan: () => get<MembershipPlanDto | null>("billing/plan", { auth: false }),
    overview: () => get<MemberOverviewDto>("billing/overview"),
    subscription: () => get<MemberSubscriptionDto>("billing/subscription"),
    subscriptions: () =>
      get<PagedResult<SubscriptionDto>>("billing/subscriptions", { query: { page: 1, pageSize: 50 } }),
    subscriptionCheckout: () => post<CheckoutResponse>("billing/subscription/checkout"),
    cancelSubscription: () => post<MemberSubscriptionDto>("billing/subscription/cancel"),
    packageCheckout: (id: string) => post<CheckoutResponse>(`billing/packages/${id}/checkout`),
    analyzerCreditsCheckout: (credits: number) => post<CheckoutResponse>("billing/analyzer-credits/checkout", { credits }),
    payments: () => get<PagedResult<PaymentDto>>("billing/payments", { query: { page: 1, pageSize: 50 } }),
    credits: () => get<CreditBalanceDto>("billing/credits"),
    creditTransactions: () =>
      get<PagedResult<CreditTransactionDto>>("billing/credits/transactions", { query: { page: 1, pageSize: 50 } }),
  },

  contact: (body: ContactRequest, turnstileToken?: string | null) =>
    post<void>("contact", body, { auth: false, turnstileToken: turnstileToken ?? null }),
};
